import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  addDoc,
  collection,
  doc,
  increment,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { Bookmark, ExternalLink, FileText, FlaskConical, Heart, MessageSquare, Share2, Waves } from 'lucide-react';
import { db, firebaseReady } from '../../lib/firebase';
import { safeGetDoc } from '../../lib/firestoreAccess';
import { togglePaperLike, togglePaperResonate, togglePaperBookmark } from '../../lib/paperEngagement';
import {
  loadPaperComments,
  buildCommentTree,
  canReplyToComment,
  MAX_COMMENT_DEPTH,
  type CommentNode,
} from '../../lib/paperComments';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../contexts/ToastContext';
import { formatTimeAgo } from '../../utils/timeAgo';
import { Button } from '../ui/Button';
import type { NewsPaperDoc } from '../../lib/newsPapers';

// ── Field colours ────────────────────────────────────────────────────────────
const FIELD_COLORS: Record<string, string> = {
  'AI / ML':      'bg-violet-500/15 text-violet-400 ring-violet-500/25',
  'Physics':      'bg-blue-500/15 text-blue-400 ring-blue-500/25',
  'Mathematics':  'bg-amber-500/15 text-amber-400 ring-amber-500/25',
  'Biology':      'bg-emerald-500/15 text-emerald-400 ring-emerald-500/25',
  'Neuroscience': 'bg-pink-500/15 text-pink-400 ring-pink-500/25',
  'Economics':    'bg-orange-500/15 text-orange-400 ring-orange-500/25',
  'Statistics':   'bg-cyan-500/15 text-cyan-400 ring-cyan-500/25',
};
function fieldBadgeClass(field: string) {
  return FIELD_COLORS[field] ?? 'bg-surface-raised text-fg-muted ring-border';
}

function pdfUrl(url: string) {
  return url.replace('arxiv.org/abs/', 'arxiv.org/pdf/');
}

// ── Comment thread sub-components ───────────────────────────────────────────
function CommentComposer({
  placeholder,
  value,
  onChange,
  onSubmit,
  onCancel,
  submitLabel = 'Send',
}: {
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  onCancel?: () => void;
  submitLabel?: string;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="min-w-0 flex-1 rounded-card border border-border bg-surface-card px-3 py-2 text-sm text-fg placeholder:text-fg-subtle"
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            onSubmit();
          }
        }}
      />
      <Button type="button" variant="outline" onClick={onSubmit}>
        {submitLabel}
      </Button>
      {onCancel ? (
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      ) : null}
    </div>
  );
}

function CommentItem({
  node,
  depth,
  viewerUid,
  replyingToId,
  replyText,
  onReply,
  onCancelReply,
  onReplyTextChange,
  onSubmitReply,
}: {
  node: CommentNode;
  depth: number;
  viewerUid: string | undefined;
  replyingToId: string | null;
  replyText: string;
  onReply: (commentId: string) => void;
  onCancelReply: () => void;
  onReplyTextChange: (text: string) => void;
  onSubmitReply: (parentId: string) => void;
}) {
  const isReplying = replyingToId === node.id;
  const canReply = depth < MAX_COMMENT_DEPTH - 1;

  return (
    <li className={depth > 0 ? 'mt-2' : undefined}>
      <div
        className={
          depth > 0
            ? 'ml-3 border-l-2 border-border pl-3 sm:ml-4 sm:pl-4'
            : undefined
        }
      >
        <div className="text-sm">
          <span className="font-medium text-fg">{node.authorName}</span>
          {node.createdAt ? (
            <span className="ml-2 text-[11px] text-fg-subtle">
              {formatTimeAgo(node.createdAt)}
            </span>
          ) : null}
          <p className="mt-0.5 whitespace-pre-wrap text-fg-muted">{node.content}</p>
        </div>
        {viewerUid && canReply ? (
          <button
            type="button"
            onClick={() => (isReplying ? onCancelReply() : onReply(node.id))}
            className="mt-1 text-xs font-medium text-brand hover:underline"
          >
            {isReplying ? 'Cancel' : 'Reply'}
          </button>
        ) : null}
        {isReplying && viewerUid ? (
          <div className="mt-2">
            <CommentComposer
              placeholder={`Reply to ${node.authorName}…`}
              value={replyText}
              onChange={onReplyTextChange}
              onSubmit={() => onSubmitReply(node.id)}
              onCancel={onCancelReply}
            />
          </div>
        ) : null}
        {node.children.length > 0 ? (
          <ul className="mt-2 space-y-2">
            {node.children.map((child) => (
              <CommentItem
                key={child.id}
                node={child}
                depth={depth + 1}
                viewerUid={viewerUid}
                replyingToId={replyingToId}
                replyText={replyText}
                onReply={onReply}
                onCancelReply={onCancelReply}
                onReplyTextChange={onReplyTextChange}
                onSubmitReply={onSubmitReply}
              />
            ))}
          </ul>
        ) : null}
      </div>
    </li>
  );
}

// ── PaperCommentThread ───────────────────────────────────────────────────────
function PaperCommentThread({
  paperId,
  viewerUid,
  onChanged,
}: {
  paperId: string;
  viewerUid: string | undefined;
  onChanged?: () => void;
}) {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [comments, setComments] = useState<Awaited<ReturnType<typeof loadPaperComments>>>([]);
  const [topLevelText, setTopLevelText] = useState('');
  const [replyingToId, setReplyingToId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');

  const load = useCallback(async () => {
    if (!db) return;
    setLoading(true);
    try {
      setComments(await loadPaperComments(paperId));
    } finally {
      setLoading(false);
    }
  }, [paperId]);

  useEffect(() => {
    void load();
  }, [load]);

  const tree = useMemo(() => buildCommentTree(comments), [comments]);

  const submit = async (text: string, parentCommentId: string | null) => {
    if (!text.trim() || !viewerUid || !db) return;
    if (parentCommentId && !canReplyToComment(comments, parentCommentId)) {
      showToast(`Replies are limited to ${MAX_COMMENT_DEPTH} levels deep.`, 'error');
      setReplyingToId(null);
      setReplyText('');
      return;
    }
    try {
      await addDoc(collection(db, 'newsPapers', paperId, 'comments'), {
        postId: paperId,
        authorId: viewerUid,
        content: text.trim(),
        parentCommentId,
        createdAt: serverTimestamp(),
      });
      await updateDoc(doc(db, 'newsPapers', paperId), { commentCount: increment(1) });
      setTopLevelText('');
      setReplyText('');
      setReplyingToId(null);
      await load();
      onChanged?.();
      showToast(parentCommentId ? 'Reply added.' : 'Comment added.', 'success');
    } catch {
      showToast('Could not post comment.', 'error');
    }
  };

  return (
    <div className="mt-4 rounded-lg border border-border bg-surface/50 p-3">
      {loading ? (
        <p className="text-xs text-fg-subtle">Loading comments…</p>
      ) : (
        <ul className="space-y-3">
          {tree.map((node) => (
            <CommentItem
              key={node.id}
              node={node}
              depth={0}
              viewerUid={viewerUid}
              replyingToId={replyingToId}
              replyText={replyText}
              onReply={(id) => {
                setReplyingToId(id);
                setReplyText('');
              }}
              onCancelReply={() => {
                setReplyingToId(null);
                setReplyText('');
              }}
              onReplyTextChange={setReplyText}
              onSubmitReply={(parentId) => void submit(replyText, parentId)}
            />
          ))}
          {tree.length === 0 && <li className="text-xs text-fg-subtle">No comments yet.</li>}
        </ul>
      )}
      {viewerUid ? (
        <div className="mt-3 border-t border-border/80 pt-3">
          <CommentComposer
            placeholder="Write a comment…"
            value={topLevelText}
            onChange={setTopLevelText}
            onSubmit={() => void submit(topLevelText, null)}
          />
        </div>
      ) : (
        <p className="mt-3 text-xs text-fg-subtle">Sign in to comment.</p>
      )}
    </div>
  );
}

// ── PaperCard ────────────────────────────────────────────────────────────────
export interface PaperCardProps {
  paper: NewsPaperDoc;
  compact?: boolean;
  onChanged?: () => void;
}

export function PaperCard({ paper, compact, onChanged }: PaperCardProps) {
  const { profile } = useAuth();
  const { showToast } = useToast();
  const viewerUid = profile?.uid;

  const [liked, setLiked] = useState(false);
  const [resonated, setResonated] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);
  const [likeCount, setLikeCount] = useState(paper.likeCount ?? 0);
  const [resonateCount, setResonateCount] = useState(paper.resonateCount ?? 0);
  const [commentCount, setCommentCount] = useState(paper.commentCount ?? 0);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [resonateLoading, setResonateLoading] = useState(false);

  // Load interaction state
  useEffect(() => {
    if (!firebaseReady || !db || !viewerUid) return;
    let cancelled = false;
    void (async () => {
      try {
        const [likeSnap, resonateSnap, bookmarkSnap] = await Promise.all([
          safeGetDoc(doc(db, 'newsPapers', paper.id, 'likes', viewerUid)),
          safeGetDoc(doc(db, 'newsPapers', paper.id, 'resonates', viewerUid)),
          safeGetDoc(doc(db, 'users', viewerUid, 'paperBookmarks', paper.id)),
        ]);
        if (cancelled) return;
        setLiked(likeSnap?.exists() ?? false);
        setResonated(resonateSnap?.exists() ?? false);
        setBookmarked(bookmarkSnap?.exists() ?? false);
      } catch {
        if (!cancelled) {
          setLiked(false);
          setResonated(false);
          setBookmarked(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [paper.id, viewerUid]);

  const handleLike = async () => {
    if (!viewerUid || !db) {
      showToast('Sign in to like papers.', 'error');
      return;
    }
    const next = !liked;
    setLiked(next);
    setLikeCount((c) => Math.max(0, c + (next ? 1 : -1)));
    try {
      await togglePaperLike(db, { paperId: paper.id, viewerUid, currentlyLiked: liked });
      onChanged?.();
    } catch {
      setLiked(!next);
      setLikeCount((c) => Math.max(0, c + (next ? -1 : 1)));
      showToast('Could not update like.', 'error');
    }
  };

  const handleResonate = async () => {
    if (!viewerUid || !db) {
      showToast('Sign in to resonate.', 'error');
      return;
    }
    if (resonateLoading) return;
    const next = !resonated;
    setResonated(next);
    setResonateCount((c) => Math.max(0, c + (next ? 1 : -1)));
    setResonateLoading(true);
    try {
      await togglePaperResonate(db, {
        paperId: paper.id,
        paper,
        viewerUid,
        currentlyResonated: resonated,
      });
      if (next) {
        showToast('Resonated! Shared to your feed.', 'success');
      } else {
        showToast('Removed from your feed.', 'info');
      }
      onChanged?.();
    } catch {
      setResonated(!next);
      setResonateCount((c) => Math.max(0, c + (next ? -1 : 1)));
      showToast('Could not update resonate.', 'error');
    } finally {
      setResonateLoading(false);
    }
  };

  const handleBookmark = async () => {
    if (!viewerUid || !db) {
      showToast('Sign in to bookmark.', 'error');
      return;
    }
    const next = !bookmarked;
    setBookmarked(next);
    try {
      await togglePaperBookmark(db, {
        paperId: paper.id,
        viewerUid,
        currentlyBookmarked: bookmarked,
        paper,
      });
      if (next) {
        showToast('Saved to bookmarks.', 'success');
      } else {
        showToast('Removed from bookmarks.', 'info');
      }
      onChanged?.();
    } catch {
      setBookmarked(!next);
      showToast('Could not update bookmark.', 'error');
    }
  };

  const handleShare = async () => {
    const url = paper.url;
    if (navigator.share) {
      try {
        await navigator.share({ title: paper.title, url });
      } catch {
        // user cancelled or not supported — silently ignore
      }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        showToast('Link copied!', 'success');
      } catch {
        showToast('Could not copy link.', 'error');
      }
    }
  };

  // ── Compact mode ────────────────────────────────────────────────────────────
  if (compact) {
    return (
      <li className="border-b border-border/40 py-2.5 last:border-0">
        <div className="min-w-0 flex-1 space-y-0.5">
          <a
            href={paper.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium leading-snug text-fg transition-colors hover:text-brand"
          >
            {paper.title}
          </a>
          {paper.authors.length > 0 && (
            <p className="text-[11px] text-fg-subtle">
              {paper.authors.slice(0, 3).join(', ')}
              {paper.authors.length > 3 ? ` +${paper.authors.length - 3}` : ''}
            </p>
          )}
          <p className="text-[10px] tabular-nums text-fg-subtle">{paper.publishedAt}</p>
        </div>
        <div className="mt-1.5 flex items-center justify-between gap-2">
          <span className="text-xs font-semibold tabular-nums text-brand">
            {paper.score.toFixed(0)} pts
          </span>
          <div className="flex items-center gap-3 text-fg-subtle">
            <button
              type="button"
              onClick={() => void handleLike()}
              aria-pressed={liked}
              className={`flex touch-manipulation items-center gap-0.5 text-xs transition-colors ${liked ? 'text-red-400' : 'hover:text-red-400'}`}
            >
              <Heart
                size={13}
                strokeWidth={1.5}
                className={liked ? 'fill-red-400' : ''}
              />
              <span className="tabular-nums">{likeCount}</span>
            </button>
            <button
              type="button"
              onClick={() => void handleResonate()}
              aria-pressed={resonated}
              disabled={resonateLoading}
              className={`flex touch-manipulation items-center gap-0.5 text-xs transition-colors ${resonated ? 'text-brand' : 'hover:text-brand'} disabled:opacity-60`}
            >
              <Waves size={13} strokeWidth={1.5} />
              <span className="tabular-nums">{resonateCount}</span>
            </button>
            <button
              type="button"
              onClick={() => void handleBookmark()}
              aria-pressed={bookmarked}
              className={`flex touch-manipulation items-center gap-0.5 text-xs transition-colors ${bookmarked ? 'text-brand' : 'hover:text-brand'}`}
            >
              <Bookmark
                size={13}
                strokeWidth={1.5}
                className={bookmarked ? 'fill-brand' : ''}
              />
            </button>
            <a
              href={pdfUrl(paper.url)}
              target="_blank"
              rel="noopener noreferrer"
              title="Open PDF"
              className="flex touch-manipulation items-center gap-0.5 rounded border border-border px-1.5 py-0.5 text-[10px] text-fg-muted transition hover:border-brand/40 hover:text-brand"
            >
              <FileText size={10} strokeWidth={1.5} />
              PDF
            </a>
          </div>
        </div>
      </li>
    );
  }

  // ── Full card mode ──────────────────────────────────────────────────────────
  const longAbstract = paper.abstract.length > 300;

  return (
    <article className="rounded-card border border-border bg-surface-card">
      <div className="p-4 space-y-3">
        {/* Header row */}
        <div className="flex flex-wrap items-center gap-1.5">
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ${fieldBadgeClass(paper.researchArea)}`}
          >
            <FlaskConical size={9} strokeWidth={2} />
            {paper.researchArea}
          </span>
          {paper.isOpenAccess && (
            <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-400 ring-1 ring-emerald-500/20">
              Open Access
            </span>
          )}
          <span className="ml-auto text-[11px] tabular-nums text-fg-subtle">
            {paper.publishedAt}
          </span>
          <span className="text-sm font-bold tabular-nums text-brand">
            {paper.score.toFixed(0)} <span className="text-[10px] font-normal text-fg-subtle">pts</span>
          </span>
        </div>

        {/* Title */}
        <a
          href={paper.url}
          target="_blank"
          rel="noopener noreferrer"
          className="group flex items-start gap-1 font-display text-lg font-semibold leading-snug text-fg hover:text-brand"
        >
          <span className="flex-1">{paper.title}</span>
          <ExternalLink
            size={13}
            strokeWidth={1.5}
            className="mt-1 shrink-0 opacity-0 transition-opacity group-hover:opacity-60"
          />
        </a>

        {/* Authors */}
        {paper.authors.length > 0 && (
          <p className="text-xs text-fg-muted">
            {paper.authors.slice(0, 4).join(', ')}
            {paper.authors.length > 4 ? ` +${paper.authors.length - 4} more` : ''}
          </p>
        )}

        {/* Abstract */}
        {paper.abstract && (
          <div>
            <p
              className={`text-sm leading-relaxed text-fg-muted ${expanded ? '' : 'line-clamp-3'}`}
            >
              {paper.abstract}
            </p>
            {longAbstract && (
              <button
                type="button"
                onClick={() => setExpanded((e) => !e)}
                className="mt-1 text-xs font-medium text-brand hover:underline"
              >
                {expanded ? 'Show less' : 'Show more'}
              </button>
            )}
          </div>
        )}

        {/* Links */}
        <div className="flex items-center gap-3">
          <a
            href={paper.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs text-brand hover:underline"
          >
            <ExternalLink size={11} strokeWidth={1.5} />
            Abstract
          </a>
          <a
            href={pdfUrl(paper.url)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 rounded-md border border-border bg-surface-raised px-2.5 py-1 text-xs font-medium text-fg-muted transition hover:border-brand/40 hover:text-brand"
          >
            <FileText size={11} strokeWidth={1.5} />
            PDF
          </a>
        </div>
      </div>

      {/* Interaction bar */}
      <div className="border-t border-border">
        <div className="grid grid-cols-5">
          {/* Like */}
          <button
            type="button"
            onClick={() => void handleLike()}
            aria-pressed={liked}
            className={`flex touch-manipulation flex-col items-center gap-0.5 py-3 text-xs font-medium transition-colors ${
              liked ? 'text-red-400' : 'text-fg-subtle hover:text-red-400'
            }`}
          >
            <Heart
              size={18}
              strokeWidth={1.5}
              className={liked ? 'fill-red-400' : ''}
            />
            <span className="tabular-nums">{likeCount}</span>
          </button>

          {/* Resonate */}
          <button
            type="button"
            onClick={() => void handleResonate()}
            aria-pressed={resonated}
            disabled={resonateLoading}
            className={`flex touch-manipulation flex-col items-center gap-0.5 py-3 text-xs font-medium transition-colors ${
              resonated ? 'text-brand' : 'text-fg-subtle hover:text-brand'
            } disabled:opacity-60`}
          >
            <Waves size={18} strokeWidth={1.5} />
            <span className="tabular-nums">{resonateCount}</span>
          </button>

          {/* Comment */}
          <button
            type="button"
            onClick={() => setCommentsOpen((o) => !o)}
            className={`flex touch-manipulation flex-col items-center gap-0.5 py-3 text-xs font-medium transition-colors ${
              commentsOpen ? 'text-brand' : 'text-fg-subtle hover:text-brand'
            }`}
          >
            <MessageSquare size={18} strokeWidth={1.5} />
            <span className="tabular-nums">{commentCount}</span>
          </button>

          {/* Share */}
          <button
            type="button"
            onClick={() => void handleShare()}
            className="flex touch-manipulation flex-col items-center gap-0.5 py-3 text-xs font-medium text-fg-subtle transition-colors hover:text-brand"
          >
            <Share2 size={18} strokeWidth={1.5} />
            <span>Share</span>
          </button>

          {/* Bookmark */}
          <button
            type="button"
            onClick={() => void handleBookmark()}
            aria-pressed={bookmarked}
            className={`flex touch-manipulation flex-col items-center gap-0.5 py-3 text-xs font-medium transition-colors ${
              bookmarked ? 'text-brand' : 'text-fg-subtle hover:text-brand'
            }`}
          >
            <Bookmark
              size={18}
              strokeWidth={1.5}
              className={bookmarked ? 'fill-brand' : ''}
            />
            <span>Save</span>
          </button>
        </div>
      </div>

      {/* Comment thread */}
      {commentsOpen && (
        <div className="border-t border-border px-4 pb-4">
          <PaperCommentThread
            paperId={paper.id}
            viewerUid={viewerUid}
            onChanged={() => {
              setCommentCount((c) => c + 1);
              onChanged?.();
            }}
          />
        </div>
      )}
    </article>
  );
}
